import { useCallback, useEffect, useMemo, useState } from "react";
import FilterMenu from "../../../components/FilterMenu";
import { ChartAreaLoader } from "../../../components/Loading";
import {
  ChartDataParams,
  FormatChartData,
} from "../../../components/recharts/functions";
import { ReMixChart } from "../../../components/recharts/ReMixCharts";
import { BrushStart, LegendKey } from "../../../models/chart_model";
import { HarayanaDiscomData } from "../../../models/rajasthan";
import {
  getHaryanaDiscomData,
  getHaryanaForecast,
  getHaryanaTrendMetrics,
} from "../../../Rest_api/RestApi";
import MetricChip, { MetricChipHead } from "../../../components/Metrics";
import { TrendData } from "../../../models/metrics";
import { ReactComponent as PeakIcon } from "../../../icons/peakicon.svg";
import { ReactComponent as AvgIcon } from "../../../icons/AvgIcon.svg";
import { ReactComponent as MinIcon } from "../../../icons/MinIcon.svg";
import { ReactComponent as TotalIcon } from "../../../icons/TotalIcon.svg";
import { ExchangeColors } from "../../Dashboard/Exchange3/FormatData";
import { H_CHART_TYPES } from "../const";
import { GROUP_BY } from "./Data";
import { renderHourTick } from "../../../components/recharts/components";
import LegendSummaryTable, {
  ChartTable,
  downloadChartTableCSV,
  downloadSummaryCSV,
} from "../../../components/table/SummaryTable";
import NewFilterContainer from "../../../components/NewFilterContainer ";
import MainPageContainer from "../../../components/MainPageContainer";
import SecondaryHeader from "../../Rajasthan/Demand/SecondaryHeader";
import Tooltip from "../../../components/tooltip";
import { useSelector } from "react-redux";
import { AddDatesWithFileName } from "../../../helpers/helper";
import BlockRangeSelector from "../../../components/BlockRangeSelector/BlockRangeSelector";
import { HaryanaPeakAnalysis } from "./TrendPeak_Analysis";
import { HaryanaSummaryAnalysis } from "./TrendSummary";
import { normalizeDisplayLabelWithoutUndefined } from "../../../utility/displayText";

let X_AXIS = [
  {
    label: "Time Slot",
    key: "time",
    values: ["block", "dateIndex"],
    isTime: true,
  },
  {
    label: "Date",
    key: "date",
    values: ["dateIndex"],
    isTime: false,
  },
  {
    label: "Month",
    key: "month",
    values: ["month"],
    isTime: false,
  },
];

// ─── X-Axis mode tabs (Intervals / Peak Analysis / Summary) ─────────────────
const X_AXIS_MODES = [
  { label: "Intervals", value: "intervals" },
  { label: "Peak Analysis", value: "peak" },
  { label: "Summary", value: "summary" },
];

// ─── Chart suffixes — single source of truth shared with sub-views ───────────
const CHART_SUFFIXES = [
  { suffix: "demand", displayName: "Actual Load", stackSuffix: "demand" },
  {
    suffix: "demand_forecast_d1",
    displayName: "Forecast D+1",
    stackSuffix: "forecast_d1",
  },
  {
    suffix: "demand_forecast_d2",
    displayName: "Forecast D+2",
    stackSuffix: "forecast_d2",
  },
  {
    suffix: "deviation",
    displayName: "Deviation %",
    stackSuffix: "deviation",
  },
];

const UNITS = ["MW", "MWh", "LU"];

export function HaryanaTrend() {
  const [TableData, setTableData] = useState<any[]>([]);
  const dataTab = useSelector((state: any) => state.viewMode.activeView);
  const [selectedUnit, setSelectedUnit] = useState(UNITS[0]);
  const [data, setData] = useState<HarayanaDiscomData[]>([]);
  const [chartKeys, setChartKeys] = useState<any[]>([]);
  const [selectedArea, setSelectedArea] = useState<any[]>([]);
  const [uniqueBlock, setUniqueBlock] = useState<any[]>([]);
  const [selectedBlock, setSelectedBlock] = useState<any[]>([]);
  const [chartData, setChartData] = useState<any[]>([]);
  const [selectedChartType, setSelectedChartType] = useState(H_CHART_TYPES[1]);
  const [selectedGroupKeys, setSelectedGroupKeys] = useState(GROUP_BY[0]);
  const [selectedXaxis, setSelectedXaxis] = useState(X_AXIS[0]);
  const [selectedXAxisMode, setSelectedXAxisMode] = useState(X_AXIS_MODES[0]);
  const [selectedForecastType, setSelectedForecastType] = useState<any[]>([
    "Forecast D+1",
  ]);
  const [loading, setLoading] = useState(true);
  const activeTab = useSelector((state: any) => state.switchTabs.activeTab);
  const [deselectedLegends, setDeselectedLegends] = useState<string[]>([]);
  const [metrics, setMetrics] = useState<TrendData | null>(null);
  const [metricsLoading, setMetricsLoading] = useState(false);
  // Unified type filter: controls which series are visible
  const TYPE_OPTIONS = [
    "Actual",
    "Forecast D+1",
    "Forecast D+2",
    "Deviation %",
  ];
  const [selectedType, setSelectedType] = useState<string[]>([
    "Actual",
    "Forecast D+1",
    "Deviation %",
  ]);
  // Suffix-based type filter for sub-views (mirrors Chhattisgarh pattern)
  const [selectedSuffix, setSelectedSuffix] = useState(
    CHART_SUFFIXES.filter((s) =>
      ["demand", "demand_forecast_d1", "deviation"].includes(s.suffix),
    ),
  );
  const DefaultDates = useSelector(
    (state: any) => state.defaultDatesSetter.defaultDates,
  );

  const createChartObject = (
    key: string,
    index: number,
    suffix: string,
    displayName: string,
    stackSuffix: string,
    selectedXaxis: { isTime: boolean },
    selectedChartType: { value: string; stackId?: string },
    ExchangeColors: string[],
  ) => {
    const displayKey = normalizeDisplayLabelWithoutUndefined(key)
      .replace(/\s*\(Forecast[^)]*\)/g, "")
      .replace(/T\+1/g, "D+1")
      .replace(/T\+2/g, "D+2");

    return {
      dataKey: `${key}_${suffix}_${selectedUnit.toLowerCase()}`,
      name: `${displayKey} ${displayName}`.trim(),
      yAxisId: suffix === "deviation" ? "right" : undefined,
      // stroke: suffix === "deviation" ? "" : undefined,
      // legendColor: suffix === "deviation" ? "" : undefined,
      type: selectedChartType.value,
      stackId:
        selectedChartType.stackId !== undefined ? stackSuffix : undefined,
    };
  };

  const _handlefetchData = useCallback(async () => {
    if (!DefaultDates[activeTab]?.defaultTrendDates) {
      return;
    }

    setLoading(true);

    try {
      const originalDates = DefaultDates[activeTab].defaultTrendDates;
      let processedDates = [...originalDates];

      const modelsToAdd = [];
      for (const date of originalDates) {
        const { label, value } = date;
        if (label.replace(" to ", ",") === value) {
          modelsToAdd.push({
            forecast: true,
            isFixed: false,
            label: label + " (Forecast)",
            value: value + ",Model 1",
            model: "Model 1",
          });
        }
      }

      processedDates = [...processedDates, ...modelsToAdd];

      const promises = processedDates.map(async (date: any) => {
        try {
          const [startDate, endDate, model] = date.value.split(",");

          if (model) {
            const forecastedData = await getHaryanaForecast(startDate, endDate);
            return forecastedData.map((item: any) => ({
              ...item,
              dates: date.label.replace(")", " ") + item.forcast_type + ")",
            }));
          } else {
            return (await getHaryanaDiscomData(startDate, endDate)).map(
              (item) => ({
                ...item,
                dates: date.label,
              }),
            );
          }
        } catch (error) {
          console.error(`Error processing date ${date.label}:`, error);
          return [];
        }
      });

      const results = await Promise.all(promises);
      const newData = results.flat();

      if (newData.length === 0) {
        console.warn("No data returned from API calls");
      }

      // Global sorted unique dates → stable day position across all ranges.
      // Without this, dateIndex ("N Day") collides/reorders across ranges and
      // the X-axis ticks render out of chronological order.
      const uniqueDates = Array.from(
        new Set(newData.map((i: any) => i.date).filter(Boolean)),
      ).sort();

      let transformedData = newData.map((item) => {
        let monthValue = "Unknown";
        if (item.date) {
          const date = new Date(item.date);
          if (!isNaN(date.getTime())) {
            monthValue = `${date.toLocaleDateString("en-US", { month: "short" })}-${date.getFullYear().toString().slice(-2)}`;
          }
        } else if (item.dates) {
          try {
            const dateMatch = item.dates.match(
              /(\d{4}-\d{2}-\d{2})|([A-Z]{3}-\d{2})/i,
            );
            if (dateMatch) {
              const dateStr = dateMatch[0];
              const date = new Date(dateStr);
              if (!isNaN(date.getTime())) {
                monthValue = `${date.toLocaleDateString("en-US", { month: "short" })}-${date.getFullYear().toString().slice(-2)}`;
              }
            }
          } catch (e) {
            console.error("Error parsing date from dates field:", e);
          }
        }

        const dayPos = item.date ? uniqueDates.indexOf(item.date) : -1;
        const dateIndex = dayPos >= 0 ? `${dayPos + 1} Day` : item.dateIndex;

        return {
          ...item,
          month: monthValue,
          dateIndex,
        };
      });

      setData([...transformedData]);
      setUniqueBlock(Array.from(new Set(newData.map((item) => item.block))));
    } catch (error) {
      console.error("Error in data fetching:", error);
    } finally {
      setLoading(false);
    }
  }, [DefaultDates, activeTab]);

  function _handleGroupByChange(index: number) {
    setSelectedGroupKeys(GROUP_BY[index]);
  }

  function handleXAxisModeChange(_item: string, index: number) {
    setSelectedXAxisMode(X_AXIS_MODES[index]);
  }

  function handleTypeChange(selected: string[]) {
    setSelectedSuffix(
      CHART_SUFFIXES.filter((s) => selected.includes(s.displayName)),
    );
  }

  function getSelectedSuffixTypes() {
    return selectedSuffix.map((s) => s.displayName);
  }

  const handleKey = useCallback(() => {
    if (selectedBlock.length > 0) {
      if (selectedXaxis.key === "time") {
        return ["dateIndex", "block"];
      } else if (selectedXaxis.key === "date") {
        return ["dateIndex"];
      } else if (selectedXaxis.key === "month") {
        return ["month"];
      }
    } else {
      if (selectedXaxis.key === "time" || selectedXaxis.key === "date") {
        return selectedXaxis.values;
      }
      return selectedXaxis.values;
    }
  }, [selectedBlock, selectedXaxis]);

  useEffect(() => {
    let filteredData = [...data];
    if (selectedBlock.length > 0) {
      filteredData = filteredData.filter((item) =>
        selectedBlock.map((opt) => opt.value).includes(item.block),
      );
    }

    // Sort chronologically so X-axis day ticks render in order.
    filteredData.sort((a: any, b: any) => {
      const d = new Date(a.date).getTime() - new Date(b.date).getTime();
      return d !== 0 ? d : a.block - b.block;
    });

    const par: ChartDataParams = {
      keys: handleKey() ?? [],
      tableKeys: ["dateIndex", "block", "month", "date"],
      groupBy: ["dates"].concat([selectedGroupKeys.toLowerCase()]),
      volumeKey: ``,
      aggregationKeys: ["demand", "demand_d_plus_one", "demand_d_plus_two"].map(
        (item) => item + "_" + selectedUnit.toLowerCase(),
      ),
      averageKeys: [
        "demand_mw",
        "demand_d_plus_one_mw",
        "demand_d_plus_two_mw",
      ],
      averageDuplicationKeys: ["block", "dateIndex", "month"],
      priceKey: "",
      data: filteredData,
    };

    const cData = FormatChartData(par);

    // Calculate deviation % for both D+1 and D+2 forecast data
    const processedData = cData.data.map((dataPoint) => {
      const newDataPoint = { ...dataPoint };

      cData.keys.forEach((key) => {
        if (key.includes("Forecast")) {
          const actualDateKey = key
            .replace(" (Forecast D+1)", "")
            .replace(" (Forecast D+2)", "");

          const actualDemandKey = `${actualDateKey}_demand_${selectedUnit.toLowerCase()}`;
          const actualDemand = dataPoint[actualDemandKey] ?? 0;

          let forecastDemandKey = "";
          if (key.includes("D+1")) {
            forecastDemandKey = `${key}_demand_d_plus_one_${selectedUnit.toLowerCase()}`;
          } else if (key.includes("D+2")) {
            forecastDemandKey = `${key}_demand_d_plus_two_${selectedUnit.toLowerCase()}`;
          }

          const forecastDemand = dataPoint[forecastDemandKey] ?? 0;

          let deviation = 0;
          if (actualDemand !== 0 && forecastDemand !== 0) {
            deviation =
              (Math.abs(actualDemand - forecastDemand) / actualDemand) * 100;
          }

          const deviationKey = `${key}_deviation_${selectedUnit.toLowerCase()}`;
          newDataPoint[deviationKey] = deviation;
        }
      });

      return newDataPoint;
    });

    setTableData(cData.tableData || []);
    setChartData(processedData);
    setChartKeys(cData.keys);
  }, [
    data,
    selectedGroupKeys,
    selectedXaxis,
    selectedUnit,
    selectedArea,
    selectedBlock,
    selectedSuffix,
    handleKey,
  ]);

  useEffect(() => {
    _handlefetchData();
  }, [_handlefetchData]);

  const fetchMetrics = useCallback(async () => {
    if (!DefaultDates[activeTab]?.defaultTrendDates?.length) {
      setMetrics(null);
      setMetricsLoading(false);
      return;
    }
    setMetricsLoading(true);

    try {
      const originalDates = DefaultDates[activeTab].defaultTrendDates;

      const ranges = originalDates
        .filter((date: any) => {
          const commaParts = date.value.split(",");
          return commaParts.length <= 2;
        })
        .map((date: any) => {
          let start: string;
          let end: string;

          if (date.value.includes(" to ")) {
            [start, end] = date.value
              .split(" to ")
              .map((s: string) => s.trim());
          } else {
            const parts = date.value.split(",").map((s: string) => s.trim());
            start = parts[0];
            end = parts[1] ?? parts[0];
          }

          const startDateStr = new Date(start).toISOString().split("T")[0];
          const endDateStr = new Date(end).toISOString().split("T")[0];

          return {
            startDate: startDateStr,
            endDate: endDateStr,
            blocks: selectedBlock.map((block: any) => block.value),
          };
        })
        .filter((range: any) => range.startDate && range.endDate);

      if (ranges.length === 0) {
        setMetrics(null);
        setMetricsLoading(false);
        return;
      }

      const discom = "Total";
      const metricsData = await getHaryanaTrendMetrics({
        ranges,
        aggregated: true,
        discom,
      });

      setMetrics(Array.isArray(metricsData) ? metricsData[0] : metricsData);
    } catch (error) {
      console.error("Error fetching metrics:", error);
      setMetrics(null);
    } finally {
      setMetricsLoading(false);
    }
  }, [DefaultDates, activeTab, selectedBlock]);

  useEffect(() => {
    fetchMetrics();
  }, [fetchMetrics]);

  function handleUnitChange(unitParam: string) {
    setSelectedUnit(unitParam);
  }

  function getLegends() {
    const showActual = selectedType.includes("Actual");
    const showD1 = selectedType.includes("Forecast D+1");
    const showD2 = selectedType.includes("Forecast D+2");
    const showDeviation = selectedType.includes("Deviation %");

    const legends = chartKeys
      .flatMap((key, index) => {
        const results = [];
        const isActual = !key.includes("Forecast");
        const isD1 =
          key.includes("Forecast D+1") ||
          key.includes("Forecast T+1") ||
          key.includes("Model");
        const isD2 =
          key.includes("Forecast D+2") || key.includes("Forecast T+2");

        if (isActual && showActual) {
          results.push(
            createChartObject(
              key,
              index,
              "demand",
              "Actual Load",
              "demand",
              selectedXaxis,
              selectedChartType as any,
              ExchangeColors,
            ),
          );
        }

        if (isD1 && showD1) {
          results.push(
            createChartObject(
              key,
              index,
              "demand_d_plus_one",
              "Forecast D+1",
              "forecast_d1",
              selectedXaxis,
              selectedChartType as any,
              ExchangeColors,
            ),
          );
        }

        if (isD2 && showD2) {
          results.push(
            createChartObject(
              key,
              index,
              "demand_d_plus_two",
              "Forecast D+2",
              "forecast_d2",
              selectedXaxis,
              selectedChartType as any,
              ExchangeColors,
            ),
          );
        }

        if (isD1 && showDeviation && showD1) {
          results.push(
            createChartObject(
              key,
              index,
              "deviation",
              "Deviation % (D+1)",
              "deviation",
              selectedXaxis,
              selectedChartType as any,
              ExchangeColors,
            ),
          );
        }

        if (isD2 && showDeviation && showD2) {
          results.push(
            createChartObject(
              key,
              index,
              "deviation",
              "Deviation % (D+2)",
              "deviation",
              selectedXaxis,
              selectedChartType as any,
              ExchangeColors,
            ),
          );
        }

        return results;
      })
      .filter((item: any) => item !== null)
      .filter(
        (item: any) =>
          !(item.name?.includes("Forecast") && item.name.includes("Schedule")),
      );

    return legends;
  }

  function summaryExtraKeys() {
    if (selectedXaxis.isTime) {
      return ["block", "dateIndex"];
    } else if (selectedXaxis.key === "date") {
      return ["dateIndex"];
    } else {
      return [selectedXaxis.values[0]];
    }
  }

  const summaryExtraLabels = selectedXaxis.isTime
    ? ["block", "dateIndex"]
    : selectedXaxis.key === "date"
      ? ["dateIndex"]
      : [selectedXaxis.values[0]];

  const handlexDataKey = useCallback(() => {
    if (selectedXaxis.key === "time") {
      if (selectedBlock.length > 0) {
        return "block";
      }
      return "block";
    } else {
      return selectedXaxis.values[0];
    }
  }, [selectedXaxis.key, selectedXaxis.values, selectedBlock.length]);

  const xDataKey = useMemo(() => handlexDataKey(), [handlexDataKey]);

  const handleLegendSelect = (selected: LegendKey[]) => {
    const allNames = getLegends().map((l: any) => l.name);
    if (selected.length === 0) {
      setDeselectedLegends([]);
    } else {
      const selectedNames = selected.map((l) => l.name);
      setDeselectedLegends(
        allNames.filter((name: string) => !selectedNames.includes(name)),
      );
    }
  };

  const currentLegends = getLegends() as any[];
  const selectedLegends =
    deselectedLegends.length === 0
      ? currentLegends
      : currentLegends.filter((l: any) => !deselectedLegends.includes(l.name));

  // For summary/table: use only the selected legends, or all if none selected
  const allLegendsTA = currentLegends;
  const visibleLegendsTA =
    selectedLegends.length > 0 && selectedLegends.length !== allLegendsTA.length
      ? allLegendsTA.filter((l: any) =>
          selectedLegends.some((al: any) => al.dataKey === l.dataKey),
        )
      : allLegendsTA;

  const commonSummaryProps = {
    legends: visibleLegendsTA as any,
    data: chartData,
    tableData: TableData,
    unit: selectedUnit,
    extraKeys: summaryExtraKeys(),
    extraLabels: summaryExtraLabels,
    filename: AddDatesWithFileName({
      fileName: "Haryana Load Trend Analysis",
      dates: DefaultDates[activeTab].defaultTrendDates,
    }),
  };

  // ── Props forwarded to Peak sub-view ─────────────────────────────────────
  const sharedSubViewProps = {
    onXAxisModeChange: handleXAxisModeChange,
    selectedUnit,
    onUnitChange: handleUnitChange,
    selectedDiscom: [] as string[],
    onDiscomChange: () => {},
    selectedGroupKeys: selectedGroupKeys,
    onGroupByChange: _handleGroupByChange,
    selectedSuffix,
    onTypeChange: handleTypeChange,
    chartSuffixes: CHART_SUFFIXES,
    metrics,
  };

  // ── Route to sub-views ────────────────────────────────────────────────────
  if (selectedXAxisMode.value === "peak") {
    return <HaryanaPeakAnalysis {...sharedSubViewProps} />;
  }
  if (selectedXAxisMode.value === "summary") {
    return <HaryanaSummaryAnalysis {...sharedSubViewProps} />;
  }

  return (
    <div className="gas-page-container">
      <NewFilterContainer>
        <div className="side-filters-container text-nowrap">
          <FilterMenu
            title="X-Axis"
            items={X_AXIS_MODES.map((item) => item.label)}
            selectedItems={[selectedXAxisMode.label]}
            onSelect={handleXAxisModeChange}
          />
          <FilterMenu
            title="Intervals"
            items={X_AXIS.map((item) => item.label)}
            selectedItems={[selectedXaxis.label]}
            onSelect={(item, index) => {
              setSelectedXaxis(X_AXIS[index]);
            }}
          />
          <FilterMenu
            title="Unit"
            items={UNITS.map((item) => item)}
            selectedItems={[selectedUnit]}
            onSelect={(item, index) => handleUnitChange(item)}
          />
          <FilterMenu
            title="Type"
            items={TYPE_OPTIONS}
            selectedItems={selectedType}
            setSelectedItems={(selected: string[]) => {
              if (selected.length === 0) {
                setSelectedType(["Actual", "Forecast D+1", "Deviation %"]);
                return;
              }
              setSelectedType(selected);
            }}
          />
          <FilterMenu
            title="Chart Type"
            items={H_CHART_TYPES.map((item) => item.label)}
            selectedItems={[selectedChartType.label]}
            onSelect={(item, index) =>
              setSelectedChartType(H_CHART_TYPES[index])
            }
          />

          {/* Disabled Filters */}
          <FilterMenu
            title="Discom"
            items={["UHBVN", "DHBVN"]}
            selectedItems={[]}
            disabled={true}
          />

          <FilterMenu
            title="Group By"
            items={["Discom Load", "Total Discom Load", "HVPN Drawal"]}
            selectedItems={[]}
            disabled={true}
          />

          <FilterMenu
            title="Type"
            items={["Schedule", "Drawal", "Load Shedding", "Total Load"]}
            selectedItems={[]}
            disabled={true}
          />
        </div>
      </NewFilterContainer>
      <MainPageContainer>
        <SecondaryHeader />
        {metrics && (
          <div className="flex flex-wrap gap-4 my-0 justify-center">
            <MetricChipHead
              icon={<PeakIcon width={20} height={19} />}
              title="Peak Load"
              items={[
                {
                  label: "Actual",
                  value: metrics.load_metrics["Peak Load (MW)"].value,
                  unit: metrics.load_metrics["Peak Load (MW)"].unit,
                },
                {
                  label: "Forecast",
                  value:
                    metrics.forecast_metrics["Peak Forecast Load (MW)"].value,
                  unit: metrics.forecast_metrics["Peak Forecast Load (MW)"]
                    .unit,
                },
              ]}
            />
            <MetricChipHead
              icon={<AvgIcon width={20} height={19} />}
              title="Average Load"
              items={[
                {
                  label: "Actual",
                  value: metrics.load_metrics["Average Load (MW)"].value,
                  unit: metrics.load_metrics["Average Load (MW)"].unit,
                },
                {
                  label: "Forecast",
                  value:
                    metrics.forecast_metrics["Average Forecast Load (MW)"]
                      .value,
                  unit: metrics.forecast_metrics["Average Forecast Load (MW)"]
                    .unit,
                },
              ]}
            />
            <MetricChipHead
              icon={<MinIcon width={20} height={19} />}
              title="Minimum Load"
              items={[
                {
                  label: "Actual",
                  value: metrics.load_metrics["Minimum Load (MW)"].value,
                  unit: metrics.load_metrics["Minimum Load (MW)"].unit,
                },
                {
                  label: "Forecast",
                  value:
                    metrics.forecast_metrics["Minimum Forecast Load (MW)"]
                      .value,
                  unit: metrics.forecast_metrics["Minimum Forecast Load (MW)"]
                    .unit,
                },
              ]}
            />
            <MetricChipHead
              icon={<TotalIcon width={20} height={19} />}
              title="Total Energy"
              items={[
                {
                  label: "Actual",
                  value: metrics.load_metrics["Total Energy (MU)"].value,
                  unit: metrics.load_metrics["Total Energy (MU)"].unit,
                },
                {
                  label: "Forecast",
                  value:
                    metrics.forecast_metrics["Total Forecast Energy (MU)"]
                      .value,
                  unit: metrics.forecast_metrics["Total Forecast Energy (MU)"]
                    .unit,
                },
              ]}
            />
          </div>
        )}
        <div className="gas-chart-container">
          {loading ? (
            <ChartAreaLoader
              title="Loading Trend Analysis"
              subtitle="Fetching HPPC Haryana load trend data…"
            />
          ) : (
            <>
              <div className="gas-heading gap-x-10">
                <h2 className="chartHeading w-60">
                  Haryana Load ({selectedUnit})
                </h2>
                <div className="flex gap-x-6 w-full justify-end">
                  <BlockRangeSelector
                    blocks={Array.from({ length: 96 }, (_, i) => i + 1)}
                    placeholder="Select Block"
                    selectedOptions={selectedBlock.map((item) => item.value)}
                    setSelectedOptions={setSelectedBlock}
                  />
                  <button
                    onClick={() => {
                      dataTab.label === "Chart" &&
                        downloadChartTableCSV(commonSummaryProps);

                      dataTab.label === "Summary" &&
                        downloadSummaryCSV(commonSummaryProps);

                      dataTab.label === "Table" &&
                        downloadChartTableCSV(commonSummaryProps);
                    }}
                    className="downloadButton"
                  >
                    Download Data
                  </button>
                </div>
              </div>

              {dataTab.label === "Chart" && (
                <ReMixChart
                  data={chartData}
                  showBrush={chartData.length > 96 ? true : undefined}
                  brushStart={BrushStart.Start}
                  brushIndex={
                    chartData.length > 96
                      ? { startIndex: 0, endIndex: 95 }
                      : undefined
                  }
                  isTimeSlot={selectedXaxis.isTime}
                  onlyTitle={true}
                  {...({ secondYAxisDomain: [0, 100] } as any)}
                  barCategoryGap={10}
                  unit={selectedUnit}
                  yAxisLabel={selectedUnit}
                  {...({ secondYAxisLabel: "Deviation %" } as any)}
                  legends={getLegends() as any}
                  xDataKey={xDataKey}
                  secondXDataKey={
                    selectedXaxis.isTime && selectedBlock.length === 0
                      ? selectedXaxis.values[1]
                      : undefined
                  }
                  xTick={
                    selectedXaxis.isTime && selectedBlock.length === 0
                      ? (value: any, index: any) => {
                          return renderHourTick(value, 10);
                        }
                      : undefined
                  }
                  defaultSelectedLegends={selectedLegends}
                  onLegendSelect={handleLegendSelect}
                />
              )}
              {dataTab.label === "Summary" && (
                <LegendSummaryTable {...commonSummaryProps} />
              )}
              {dataTab.label === "Table" && (
                <ChartTable
                  {...commonSummaryProps}
                  defaultSortDirection="asc"
                />
              )}
            </>
          )}
        </div>
        <div className="gna-footer-container">
          <div className="grid grid-cols-3 justify-between gap-5 ">
            <Tooltip note={<p>HPPC</p>} text=" ">
              <p className="gna-footer-text">Source</p>
            </Tooltip>
            <p className="gna-footer-text col-span-1 text-center">
              Powered by <strong>GNA Energy</strong>
            </p>
            <strong className="gna-footer-text col-span-1 text-end">
              Beta Version
            </strong>
          </div>
        </div>
      </MainPageContainer>
    </div>
  );
}
